/**
 * The one door from the prospects pages to their data: `/api/v1/prospecting`.
 *
 * Ported from radar `src/components/api.ts` (`api()`) and
 * `src/components/sequences/common.tsx` (`call()`), with the same behaviour a
 * page relied on there, moved onto this app's sign-in:
 *
 *   - `GET` unless a body is given, which makes it `POST`. A plain object is
 *     sent as JSON, `FormData`, `URLSearchParams` and `Blob` as they are, so an
 *     upload reaches the service byte for byte (the gateway never parses it).
 *   - Never cached by the browser (`cache: 'no-store'`), as the old client.
 *   - A refusal becomes a `ProspectingError` whose message is the service's own
 *     sentence (`{ error: "..." }`), or the gateway's (`{ error, code }`), or
 *     this app's envelope (`{ error: { code, message } }`), and only when none
 *     of those is there `Request failed (<status>)`, the old fallback.
 *   - Signed out (the gateway's 401, `code: UNAUTHENTICATED`): the cached
 *     session is dropped the way `useSessionExpiryWatcher` drops it, so
 *     `RequireAuth` sends the person to this app's own sign-in screen and back.
 *     The old client sent the browser to `/prospector/login` instead.
 *   - Signed in but not allowed (the service's 401 or its "cannot open" 403):
 *     a `no-access` error, which `ProspectingAccess` turns into one sentence.
 *
 * `prospectingCall` is `call()`: the same request, with a refusal handed back
 * as a value carrying its code and details instead of thrown, for the pages
 * that show a refusal as the normal answer (a launch the service refuses).
 *
 * Query keys live here too, so every page caches under the same root and a
 * change on one page can refresh another by prefix. See PORTING.md.
 */
import { buildQuery, type QueryParams } from '../../../lib/api'
import { qk, queryClient } from '../../../lib/queryClient'

/** Where the browser reaches the prospects service. Nothing else is ever called. */
export const PROSPECTING_API_BASE = '/api/v1/prospecting'

/**
 * Where the images emails link live: the brand marks and platform icons the
 * service serves under its old prefix, which the gateway passes through to
 * anybody. `brandArt()` and `platformIcon()` (lib/brand-assets.ts) answer paths
 * relative to it.
 */
export const PROSPECTING_ASSET_BASE = '/prospector/'

/** `/api/v1/prospecting/posts/image?u=...`, for an href or an img src that the browser loads itself. */
export function prospectingUrl(path: string, params?: QueryParams): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${PROSPECTING_API_BASE}${suffix}${buildQuery(params)}`
}

/** `/prospector/brands/firmadapt-mark.png` from `brands/firmadapt-mark.png`. */
export function prospectingAsset(relative: string): string {
  return `${PROSPECTING_ASSET_BASE}${relative.replace(/^\/+/, '')}`
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * What kind of refusal it was, which is what a page decides on.
 *
 *   signed-out   this app's session is gone, so its sign-in screen takes over
 *   no-access    signed in, and this account has no seat in Prospecting
 *   forbidden    signed in with a seat, and this one thing is not theirs to do
 *   not-found    the thing asked for is not there
 *   unreachable  the network, or the service did not answer (502, 503, 504)
 *   failed       anything else the service refused, with its own sentence
 */
export type ProspectingErrorKind = 'signed-out' | 'no-access' | 'forbidden' | 'not-found' | 'unreachable' | 'failed'

export class ProspectingError extends Error {
  readonly status: number
  readonly kind: ProspectingErrorKind
  /** The machine code when one came with the refusal: the gateway's, or the GojiB code the service passed on. */
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(init: { status: number; kind: ProspectingErrorKind; message: string; code?: string; details?: Record<string, unknown> }) {
    super(init.message)
    this.name = 'ProspectingError'
    this.status = init.status
    this.kind = init.kind
    this.code = init.code ?? ''
    this.details = init.details ?? {}
  }

  /** True when trying again in a moment could work. */
  get isTransient(): boolean {
    return this.kind === 'unreachable' || this.status === 429
  }
}

export function isProspectingError(value: unknown): value is ProspectingError {
  return value instanceof ProspectingError
}

/** The sentence the person reads when they have no seat. One sentence, the same everywhere. */
export const NO_ACCESS_MESSAGE = 'This account cannot open Prospecting. Ask Basel to add you.'

/** What the browser says when it could not reach the server at all. */
export const OFFLINE_MESSAGE = 'Prospecting did not answer. Check your connection and try again.'

/**
 * The service's sentences, in this app's words.
 *
 * Its messages predate the merge and name the parts as separate apps
 * ("Prospector", "GojiBasel", "GojiB"). The person is in one app now, called
 * Son of Basel since 2026-10-30, so a sentence is read in those words until the
 * service's own copy catches up.
 */
export function inAppWords(message: string): string {
  return message.replace(/\bProspector\b/g, 'Prospecting').replace(/\b(?:GojiBasel|GojiB|GoGB)\b/g, 'Son of Basel')
}

/** The `{error}`, `{error, code}` or `{error: {code, message, details}}` a refusal carried, read whichever it was. */
function refusalOf(payload: unknown): { message: string | null; code: string; details: Record<string, unknown> } {
  if (typeof payload !== 'object' || payload === null) return { message: null, code: '', details: {} }
  const body = payload as { error?: unknown; code?: unknown; details?: unknown; message?: unknown }
  const objectDetails = (value: unknown): Record<string, unknown> => (typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {})
  if (typeof body.error === 'string' && body.error.trim() !== '') {
    return { message: body.error, code: typeof body.code === 'string' ? body.code : '', details: objectDetails(body.details) }
  }
  if (typeof body.error === 'object' && body.error !== null) {
    const inner = body.error as { code?: unknown; message?: unknown; details?: unknown }
    return {
      message: typeof inner.message === 'string' && inner.message.trim() !== '' ? inner.message : null,
      code: typeof inner.code === 'string' ? inner.code : '',
      details: objectDetails(inner.details),
    }
  }
  return { message: null, code: typeof body.code === 'string' ? body.code : '', details: objectDetails(body.details) }
}

/** A sentence that says the account has no seat, in either wording the service has used. */
const NO_SEAT = /cannot open (prospector|prospecting)|not on this account|no seat/i

/**
 * A refusal, sorted into the kind a page decides on.
 *
 * Pure, so the sorting is covered without a network. The gateway's own 401
 * carries `code: UNAUTHENTICATED` and means this app's session is gone. A 401
 * without it came from the service, which only says that to somebody this
 * app signed in and the service has no seat for.
 */
export function prospectingErrorFrom(status: number, payload: unknown): ProspectingError {
  const refusal = refusalOf(payload)
  const message = refusal.message === null ? `Request failed (${status})` : inAppWords(refusal.message)
  const base = { status, code: refusal.code, details: refusal.details }
  if (status === 401) {
    return refusal.code === 'UNAUTHENTICATED' || refusal.code === 'UNAUTHORIZED'
      ? new ProspectingError({ ...base, kind: 'signed-out', message: 'Your session has ended. Sign in again.' })
      : new ProspectingError({ ...base, kind: 'no-access', message: NO_ACCESS_MESSAGE })
  }
  if (status === 403) {
    return NO_SEAT.test(refusal.message ?? '')
      ? new ProspectingError({ ...base, kind: 'no-access', message: NO_ACCESS_MESSAGE })
      : new ProspectingError({ ...base, kind: 'forbidden', message })
  }
  if (status === 404) return new ProspectingError({ ...base, kind: 'not-found', message })
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return new ProspectingError({ ...base, kind: 'unreachable', message: refusal.message === null ? OFFLINE_MESSAGE : message })
  }
  return new ProspectingError({ ...base, kind: 'failed', message })
}

/**
 * This app's session is gone: drop what the cache holds and ask for the
 * session again, which comes back 401 and lets `RequireAuth` send the person
 * to the sign-in screen with this page remembered. The same two steps
 * `useSessionExpiryWatcher` takes for the rest of the console.
 */
function signedOut(): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
  void queryClient.invalidateQueries({ queryKey: qk.session() })
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export type ProspectingBody = Record<string, unknown> | unknown[] | FormData | URLSearchParams | Blob

export interface ProspectingRequestOptions {
  /** GET by default, POST when a body is given. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** A plain object or array is sent as JSON. FormData, URLSearchParams and Blob are sent as they are. */
  body?: ProspectingBody
  /** Added to the path as a query string. Arrays repeat the key. */
  params?: QueryParams
  signal?: AbortSignal
  headers?: Record<string, string>
}

function isRawBody(body: ProspectingBody): body is FormData | URLSearchParams | Blob {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  )
}

/** The fetch options for one request. Exported for the test, which checks the method and body rules. */
export function requestInit(options: ProspectingRequestOptions): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  let body: BodyInit | undefined
  if (options.body !== undefined) {
    if (isRawBody(options.body)) {
      // The browser sets the multipart boundary and the form type itself.
      body = options.body
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(options.body)
    }
  }
  return {
    method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    cache: 'no-store',
    headers,
    body,
    signal: options.signal,
  }
}

/** The answer's body: JSON when it is JSON, the text when it is not, and `{}` when there is none. */
async function readAnswer(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '')
  if (text === '') return {}
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { error: response.ok ? undefined : text.slice(0, 300) }
  }
}

/**
 * One request, thrown on refusal. `api()` in the old pages.
 *
 * ```ts
 * const { brands } = await prospectingApi<{ brands: BrandRow[] }>('/brands')
 * await prospectingApi(`/opps/${id}`, { method: 'PATCH', body: { status: 'skipped' } })
 * ```
 */
export async function prospectingApi<T = unknown>(path: string, options: ProspectingRequestOptions = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(prospectingUrl(path, options.params), requestInit(options))
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ProspectingError({ status: 0, kind: 'unreachable', message: OFFLINE_MESSAGE })
  }
  const data = await readAnswer(response)
  if (!response.ok) {
    const error = prospectingErrorFrom(response.status, data)
    if (error.kind === 'signed-out') signedOut()
    throw error
  }
  return data as T
}

/** A refusal handed back as a value: `call()` in the old Sequences page. */
export type ProspectingAnswer<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; code: string; message: string; details: Record<string, unknown>; kind: ProspectingErrorKind }

/** One request, with a refusal as a value carrying its code and details rather than a thrown sentence. */
export async function prospectingCall<T = unknown>(path: string, options: ProspectingRequestOptions = {}): Promise<ProspectingAnswer<T>> {
  try {
    const data = await prospectingApi<T>(path, options)
    return { ok: true, status: 200, data }
  } catch (error) {
    if (!isProspectingError(error)) throw error
    return { ok: false, status: error.status, error: error.message, code: error.code, message: error.message, details: error.details, kind: error.kind }
  }
}

/** The verbs, for the pages that read better with them. */
export const prospecting = {
  get: <T = unknown>(path: string, options: Omit<ProspectingRequestOptions, 'method' | 'body'> = {}) => prospectingApi<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(path: string, body?: ProspectingBody, options: Omit<ProspectingRequestOptions, 'method' | 'body'> = {}) =>
    prospectingApi<T>(path, { ...options, method: 'POST', ...(body === undefined ? {} : { body }) }),
  put: <T = unknown>(path: string, body?: ProspectingBody, options: Omit<ProspectingRequestOptions, 'method' | 'body'> = {}) =>
    prospectingApi<T>(path, { ...options, method: 'PUT', ...(body === undefined ? {} : { body }) }),
  patch: <T = unknown>(path: string, body?: ProspectingBody, options: Omit<ProspectingRequestOptions, 'method' | 'body'> = {}) =>
    prospectingApi<T>(path, { ...options, method: 'PATCH', ...(body === undefined ? {} : { body }) }),
  delete: <T = unknown>(path: string, body?: ProspectingBody, options: Omit<ProspectingRequestOptions, 'method' | 'body'> = {}) =>
    prospectingApi<T>(path, { ...options, method: 'DELETE', ...(body === undefined ? {} : { body }) }),
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/** The root every prospects query sits under, so one call can refresh them all. */
export const PROSPECTING_QUERY_ROOT = 'prospecting'

/**
 * The cache key for a path and its query: `['prospecting', 'review', {brand: 'all'}]`.
 *
 * One segment per path segment, so `['prospecting', 'review']` refreshes every
 * review read whatever its query, and `['prospecting']` refreshes the lot. The
 * query is the last element as an object, so the order its keys were written
 * in does not make two keys for one read.
 */
export function prospectingKey(path: string, params?: Record<string, unknown>): readonly unknown[] {
  const segments = path
    .split('?')[0]!
    .split('/')
    .filter((segment) => segment !== '')
  return params === undefined ? [PROSPECTING_QUERY_ROOT, ...segments] : [PROSPECTING_QUERY_ROOT, ...segments, params]
}

/** The keys more than one page reads. */
export const prospectingQueryKeys = {
  all: () => [PROSPECTING_QUERY_ROOT] as const,
  /** GET /me: who is signed in, their role, brands and own settings. */
  me: () => [PROSPECTING_QUERY_ROOT, 'me'] as const,
  /** GET /brands: the brands this person sees, with their counts. */
  brands: () => [PROSPECTING_QUERY_ROOT, 'brands'] as const,
  /** The sidebar's three counts, per workspace brand. */
  counts: (scope: string | null) => [PROSPECTING_QUERY_ROOT, 'counts', scope] as const,
}

/** Retry a read only when the service was out of reach, twice at most, as the rest of the console does. */
export function prospectingRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 2 && isProspectingError(error) && error.isTransient
}
